import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiSearch, FiFilter,
  FiCalendar, FiMapPin, FiUser, FiDollarSign, FiPrinter,
  FiChevronLeft, FiChevronRight, FiX, FiCheck,
  FiFileText, FiPackage, FiTool, FiShield,
} from 'react-icons/fi';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import API from '../api/axios';
import { toast } from 'react-toastify';
import './Projects2.css';

const STATUS_CONFIG = {
  planning:    { label: 'Planning',    color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
  in_progress: { label: 'In Progress', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
  review:      { label: 'Review',      color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
  completed:   { label: 'Completed',   color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0' },
  on_hold:     { label: 'On Hold',     color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
};

const STAGES = [
  { key: 'site_visit',         label: 'Site Visit',         icon: '📍' },
  { key: 'material_planning',  label: 'Material Planning',  icon: '📦' },
  { key: 'quotation',          label: 'Quotation',          icon: '📄' },
  { key: 'purchase',           label: 'Purchase',           icon: '🛒' },
  { key: 'execution',          label: 'Execution',          icon: '👷' },
  { key: 'quality_check',      label: 'Quality Check',      icon: '✅' },
  { key: 'payment_collection', label: 'Payment Collection', icon: '💰' },
  { key: 'closure',            label: 'Closure',            icon: '🏁' },
];

const AVATAR_COLORS = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899','#14b8a6'];
const avatarColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
const initials    = (t) => t?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
const fmt         = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtInput    = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';

const PAGE_SIZE = 8;

const emptyForm = {
  title: '', description: '', status: 'planning', client: '', budget: '', quotationBudget: 0,
  style: '', address: '', startDate: '', endDate: '',
  stages: STAGES.map(s => ({ key: s.key, status: 'Pending', date: '' })),
  team: [], linkedQuotation: '',
};

export default function Projects() {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects]       = useState([]);
  const [clients, setClients]         = useState([]);
  const [users, setUsers]             = useState([]);
  const [roles, setRoles]             = useState([]);
  const [selected, setSelected]       = useState(null);
  const [search, setSearch]           = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilterDrop, setShowFilterDrop] = useState(false);
  const [page, setPage]               = useState(1);
  const [showModal, setShowModal]     = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(emptyForm);
  const [savingStage, setSavingStage] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [clientQuotations, setClientQuotations] = useState([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);

  const fetchProjects = useCallback((keepSelected = false) => {
    API.get('/projects').then(r => {
      setProjects(r.data);
      if (keepSelected) {
        setSelected(prev => prev ? (r.data.find(p => p._id === prev._id) || r.data[0] || null) : null);
      } else {
        setSelected(prev => prev || r.data[0] || null);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => {
    API.get('/clients').then(r => setClients(r.data)).catch(() => {});
    API.get('/users').then(r => setUsers(r.data)).catch(() => {});
    API.get('/roles').then(r => setRoles(r.data)).catch(() => {});
  }, []);

  // Auto-open new project modal when navigated from Customer page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clientId = params.get('clientId');
    if (clientId) {
      setForm({ ...emptyForm, client: clientId });
      setClientQuotations([]);
      setEditing(null);
      setShowModal(true);
      API.get(`/quotations?client=${clientId}`).then(r => setClientQuotations(r.data || [])).catch(() => {});
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh selected project from API when navigating back to this page
  // so linkedQuotation and quotationBudget reflect latest data
  useEffect(() => {
    if (selected?._id) {
      API.get(`/projects/${selected._id}`).then(r => {
        setSelected(r.data);
        setProjects(prev => prev.map(p => p._id === r.data._id ? r.data : p));
      }).catch(() => {});
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = projects.filter(p => {
    const s = search.toLowerCase();
    const matchSearch = !s || p.title?.toLowerCase().includes(s) ||
      p.client?.name?.toLowerCase().includes(s) || p.address?.toLowerCase().includes(s);
    const matchStatus = !filterStatus || p.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setClientQuotations([]); setShowModal(true); };

  const openEdit = async (p) => {
    setEditing(p);
    const clientId = p.client?._id || p.client || '';
    setForm({
      title: p.title, description: p.description || '', status: p.status,
      client: clientId, budget: p.budget || '', quotationBudget: p.quotationBudget || 0,
      style: p.style || 'Modern', address: p.address || '',
      startDate: fmtInput(p.startDate), endDate: fmtInput(p.endDate),
      stages: STAGES.map(s => {
        const found = p.stages?.find(x => x.key === s.key);
        return found ? { key: s.key, status: found.status, date: fmtInput(found.date) } : { key: s.key, status: 'Pending', date: '' };
      }),
      team: (p.team || []).map(t => ({ role: t.role || '', user: t.user?._id || t.user || '' })),
      linkedQuotation: p.linkedQuotation || '',
    });
    setClientQuotations([]);
    if (clientId) {
      try {
        const r = await API.get(`/quotations?client=${clientId}`);
        setClientQuotations(r.data || []);
      } catch { setClientQuotations([]); }
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Project title is required');
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      return toast.error('Expected Completion cannot be before Start Date');
    }
    const assignedUsers = form.team.map(m => m.user).filter(Boolean);
    if (new Set(assignedUsers).size !== assignedUsers.length) {
      return toast.error('The same team member is assigned more than once');
    }
    try {
      let res;
      const payload = {
        ...form,
        linkedQuotation: form.linkedQuotation || undefined,
      };
      if (editing) {
        res = await API.put(`/projects/${editing._id}`, payload);
        toast.success('Project updated');
      } else {
        res = await API.post('/projects', payload);
        toast.success('Project created');
      }
      setSelected(res.data);
      setShowModal(false);
      fetchProjects(true);
    } catch (err) { toast.error(err.response?.data?.message || 'Error saving project'); }
  };

  const addTeamMember   = () => setForm(f => ({ ...f, team: [...f.team, { role: '', user: '' }] }));
  const removeTeamMember = (idx) => setForm(f => ({ ...f, team: f.team.filter((_, i) => i !== idx) }));
  const updateTeam = (idx, field, value) => {
    const team = [...form.team];
    team[idx] = { ...team[idx], [field]: value };
    setForm(f => ({ ...f, team }));
  };

  // Only site_visit and closure can be manually updated from this page
  const MANUAL_STAGE_KEYS = ['site_visit', 'closure'];

  const quickUpdateStage = async (stageKey, newStatus) => {
    if (!selected) return;
    if (!MANUAL_STAGE_KEYS.includes(stageKey)) return; // guard — only manual stages allowed
    setSavingStage(stageKey);
    const stages = STAGES.map(s => {
      const existing = selected.stages?.find(x => x.key === s.key);
      if (s.key === stageKey) return { key: s.key, status: newStatus, date: newStatus === 'Completed' ? new Date().toISOString().slice(0, 10) : (existing?.date || '') };
      return existing ? { key: s.key, status: existing.status, date: fmtInput(existing.date) } : { key: s.key, status: 'Pending', date: '' };
    });
    try {
      const res = await API.put(`/projects/${selected._id}`, { stages });
      setSelected(res.data);
      setProjects(prev => prev.map(p => p._id === res.data._id ? res.data : p));
      const label = STAGES.find(s => s.key === stageKey)?.label || stageKey;
      toast.success(`${label} → ${newStatus}`);
    } catch { toast.error('Failed to update stage'); }
    setSavingStage(null);
  };

  // Print / PDF
  const printProject = () => {
    if (!selected) return;
    const logoUrl = `${window.location.origin}/cmpy_logo.png`;
    const stagesHtml = selStages.map(s => `
      <tr>
        <td>${s.icon} ${s.label}</td>
        <td><span style="padding:2px 10px;border-radius:999px;font-size:11px;font-weight:600;
          background:${s.status === 'Completed' ? '#f0fdf4' : s.status === 'In Progress' ? '#fffbeb' : '#f8fafc'};
          color:${s.status === 'Completed' ? '#16a34a' : s.status === 'In Progress' ? '#d97706' : '#94a3b8'}">
          ${s.status}
        </span></td>
        <td>${s.date ? fmt(s.date) : '—'}</td>
      </tr>`).join('');
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${projectId} — ${selected.title}</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#222;font-size:13px}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #2563eb;padding-bottom:12px;margin-bottom:16px}
      .header img{height:48px;width:auto;object-fit:contain}
      .header-title{text-align:right}
      .header-title h1{font-size:20px;font-weight:800;color:#2563eb;margin:0}
      .header-title p{font-size:12px;color:#64748b;margin:2px 0 0}
      .meta{display:flex;flex-wrap:wrap;gap:16px;padding:12px;background:#f8fafc;border-radius:8px;margin-bottom:16px;border:1px solid #e2e8f0}
      .meta-cell{display:flex;flex-direction:column;gap:2px;min-width:120px}
      .meta-cell span{font-size:10px;color:#94a3b8;text-transform:uppercase}
      .meta-cell strong{font-size:13px}
      table{width:100%;border-collapse:collapse;margin:12px 0}
      th{background:#2563eb;color:#fff;padding:8px 12px;text-align:left;font-size:11px}
      td{padding:7px 12px;border-bottom:1px solid #f1f5f9}
      h3{font-size:13px;font-weight:700;color:#0f172a;margin:16px 0 8px;text-transform:uppercase;letter-spacing:.5px}
      .footer{margin-top:24px;padding-top:10px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8}
      @media print{button{display:none}}
    </style></head><body>
      <div class="header">
        <div style="display:flex;align-items:center;gap:12px">
          <img src="${logoUrl}" alt="Logo" onerror="this.style.display='none'" />
          <div><div style="font-size:18px;font-weight:700">Interior Design Co.</div><div style="font-size:11px;color:#64748b">Project Report</div></div>
        </div>
        <div class="header-title"><h1>${projectId}</h1><p>${selected.title}</p></div>
      </div>
      <div class="meta">
        <div class="meta-cell"><span>Customer</span><strong>${selected.client?.name || selClient?.name || '—'}</strong></div>
        <div class="meta-cell"><span>Created By</span><strong>${selected.designer?.name || '—'}</strong></div>
        <div class="meta-cell"><span>Status</span><strong>${STATUS_CONFIG[selected.status]?.label || '—'}</strong></div>
        <div class="meta-cell"><span>Final Budget</span><strong>${selected.budget ? '₹' + Number(selected.budget).toLocaleString('en-IN') : '—'}</strong></div>
        ${selected.quotationBudget ? `<div class="meta-cell"><span>Quotation Budget</span><strong>₹${Number(selected.quotationBudget).toLocaleString('en-IN')}</strong></div>` : ''}
        <div class="meta-cell"><span>Style</span><strong>${selected.style || '—'}</strong></div>
        <div class="meta-cell"><span>Start Date</span><strong>${fmt(selected.startDate)}</strong></div>
        <div class="meta-cell"><span>Completion</span><strong>${fmt(selected.endDate)}</strong></div>
        ${selected.address ? `<div class="meta-cell"><span>Location</span><strong>${selected.address}</strong></div>` : ''}
      </div>
      <h3>Project Stages</h3>
      <table>
        <thead><tr><th>Stage</th><th>Status</th><th>Date</th></tr></thead>
        <tbody>${stagesHtml}</tbody>
      </table>
      ${selected.description ? `<h3>Notes</h3><p style="color:#475569;line-height:1.6">${selected.description}</p>` : ''}
      <div class="footer">
        <span>Generated on ${fmt(new Date().toISOString())}</span>
        <span>Completed: ${completedCount}/${STAGES.length} stages (${Math.round(completedCount / STAGES.length * 100)}%)</span>
      </div>
      <script>window.onload=()=>{window.print();}${'<'}/script>
    </body></html>`);
    win.document.close();
  };

  const selStages = selected ? STAGES.map(s => ({
    ...s, ...(selected.stages?.find(x => x.key === s.key) || { status: 'Pending', date: '' })
  })) : [];

  const completedCount  = selStages.filter(s => s.status === 'Completed').length;
  const inProgressCount = selStages.filter(s => s.status === 'In Progress').length;
  const pendingCount    = STAGES.length - completedCount - inProgressCount;
  const selStatus  = selected ? (STATUS_CONFIG[selected.status] || STATUS_CONFIG.planning) : null;
  const selClient  = clients.find(c => c._id === (selected?.client?._id || selected?.client));
  const projectId  = selected ? (selected.projectId || `PRJ-${String(selected._id).slice(-5).toUpperCase()}`) : '';

  return (
    <Layout>
      {loading && <PageLoader text="Loading projects..." />}
      <div className="pm-root" onClick={() => setShowFilterDrop(false)}>

        {/* ── LEFT PANEL ── */}
        <aside className="pm-sidebar">
          <div className="pm-sidebar-head">
            <div className="pm-sidebar-title-row">
              <h2>All Projects</h2>
              <span className="pm-sidebar-count">({filtered.length})</span>
            </div>
            <div className="pm-sidebar-btns">
              <div style={{ position: 'relative' }}>
                <button className={`pm-icon-btn ${filterStatus ? 'pm-icon-btn-active' : ''}`} title="Filter by status"
                  onClick={e => { e.stopPropagation(); setShowFilterDrop(v => !v); }}>
                  <FiFilter size={14} />
                </button>
                {showFilterDrop && (
                  <div className="pm-filter-drop" onClick={e => e.stopPropagation()}>
                    <button className={!filterStatus ? 'pm-fd-active' : ''} onClick={() => { setFilterStatus(''); setPage(1); setShowFilterDrop(false); }}>
                      All Statuses
                    </button>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <button key={k} className={filterStatus === k ? 'pm-fd-active' : ''}
                        style={{ color: v.color }}
                        onClick={() => { setFilterStatus(k); setPage(1); setShowFilterDrop(false); }}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button className="pm-new-btn" onClick={openAdd}><FiPlus size={13} /> New Project</button>
            </div>
          </div>

          <div className="pm-search-wrap">
            <FiSearch size={13} className="pm-search-icon" />
            <input className="pm-search-input" placeholder="Search projects..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>

          <div className="pm-list">
            {paginated.map(p => {
              const st  = STATUS_CONFIG[p.status] || STATUS_CONFIG.planning;
              const pid = p.projectId || `PRJ-${String(p._id).slice(-5).toUpperCase()}`;
              return (
                <div key={p._id} className={`pm-list-item ${selected?._id === p._id ? 'active' : ''}`}
                  onClick={() => setSelected(p)}>
                  <div className="pm-list-thumb" style={{ background: avatarColor(p.title) }}>
                    {initials(p.title)}
                  </div>
                  <div className="pm-list-info">
                    <span className="pm-list-pid">{pid}</span>
                    <p className="pm-list-name">{p.title}</p>
                    <p className="pm-list-client">{p.client?.name || '—'}</p>
                  </div>
                  <span className="pm-list-badge" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                    {st.label}
                  </span>
                </div>
              );
            })}
            {paginated.length === 0 && <p className="pm-list-empty">No projects found</p>}
          </div>

          {totalPages > 1 && (
            <div className="pm-pagination">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><FiChevronLeft size={13} /></button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i} className={page === i + 1 ? 'active' : ''} onClick={() => setPage(i + 1)}>{i + 1}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><FiChevronRight size={13} /></button>
            </div>
          )}
        </aside>

        {/* ── RIGHT DETAIL ── */}
        {selected ? (
          <main className="pm-detail" onClick={e => e.stopPropagation()}>
            <div className="pm-detail-topbar">
              <div className="pm-detail-title-row">
                <h1>{projectId} — {selected.title}</h1>
                <span className="pm-status-pill" style={{ background: selStatus?.bg, color: selStatus?.color, border: `1px solid ${selStatus?.border}` }}>
                  <span className="pm-sdot" style={{ background: selStatus?.color }} />{selStatus?.label}
                </span>
              </div>
              <div className="pm-detail-actions">
                <button className="pm-action-btn" onClick={() => openEdit(selected)}><FiEdit2 size={13} /> Edit Project</button>
                <button className="pm-action-btn" onClick={printProject}><FiPrinter size={13} /> Print</button>
                {(() => {
                  // Helper: get stage status for a key
                  const stageStatus = (key) => selected.stages?.find(s => s.key === key)?.status || 'Pending';
                  const isDone = (key) => stageStatus(key) === 'Completed';

                  // Quotation: if linkedQuotation exists → view it, else create new
                  const hasQuotation = !!selected.linkedQuotation;
                  const quoId = selected.linkedQuotation?._id || selected.linkedQuotation;

                  // Flow gates
                  const quoDone  = isDone('quotation');
                  const matDone  = isDone('material_planning');
                  const purDone  = isDone('purchase');
                  const exeDone  = isDone('execution');

                  const FlowBtn = ({ label, icon, onClick, disabled, title, className }) => (
                    <button
                      className={`pm-action-btn pm-flow-btn ${className || ''} ${disabled ? 'pm-flow-disabled' : ''}`}
                      title={title}
                      onClick={disabled ? undefined : onClick}
                      style={disabled ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
                    >
                      {icon} {label}
                    </button>
                  );

                  return (<>
                    {/* Materials — always accessible */}
                    <FlowBtn
                      label="Materials" icon={<FiPackage size={13} />}
                      className="pm-flow-mat"
                      disabled={false}
                      title="Material Planning"
                      onClick={() => navigate(`/material-planning?projectId=${selected._id}`)}
                    />

                    {/* Quotation — requires material planning completed; smart navigate */}
                    <FlowBtn
                      label={hasQuotation ? 'Quotation' : 'Quotation'} icon={<FiFileText size={13} />}
                      className="pm-flow-quo"
                      disabled={!matDone}
                      title={matDone ? (hasQuotation ? `View Quotation ${selected.linkedQuotation?.quotationNumber || ''}` : 'Create Quotation') : 'Complete Material Planning first'}
                      onClick={() => hasQuotation
                        ? navigate(`/quotations?viewId=${quoId}`)
                        : navigate(`/quotations?projectId=${selected._id}&clientId=${selected.client?._id || ''}&clientName=${encodeURIComponent(selected.client?.name || '')}`)
                      }
                    />

                    {/* Purchase — requires quotation completed */}
                    <FlowBtn
                      label="Purchase" icon={<FiPackage size={13} />}
                      className="pm-flow-pur"
                      disabled={!quoDone}
                      title={quoDone ? 'Purchase Orders' : 'Complete Quotation stage first'}
                      onClick={() => navigate(`/purchase?projectId=${selected._id}`)}
                    />

                    {/* Execution — requires purchase completed */}
                    <FlowBtn
                      label="Execution" icon={<FiTool size={13} />}
                      className="pm-flow-exe"
                      disabled={!purDone}
                      title={purDone ? 'Site Execution' : 'Complete Purchase first'}
                      onClick={() => navigate(`/site-execution?projectId=${selected._id}`)}
                    />

                    {/* Quality — requires execution completed */}
                    <FlowBtn
                      label="Quality" icon={<FiShield size={13} />}
                      className="pm-flow-qc"
                      disabled={!exeDone}
                      title={exeDone ? 'Quality Check' : 'Complete Execution first'}
                      onClick={() => navigate(`/quality-check?projectId=${selected._id}`)}
                    />
                  </>);
                })()}
              </div>
            </div>

            {/* Meta strip */}
            <div className="pm-meta-strip">
              <div className="pm-meta-cell">
                <FiUser size={13} className="pm-meta-ico" />
                <div><p className="pm-meta-lbl">Customer</p><p className="pm-meta-val">{selected.client?.name || selClient?.name || '—'}</p></div>
              </div>
              <div className="pm-meta-cell">
                <FiDollarSign size={13} className="pm-meta-ico" />
                <div>
                  <p className="pm-meta-lbl">Final Budget</p>
                  <p className="pm-meta-val">{selected.budget ? `₹ ${Number(selected.budget).toLocaleString('en-IN')}` : '—'}</p>
                  {selected.quotationBudget > 0 && (
                    <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>
                      Quotation: ₹{Number(selected.quotationBudget).toLocaleString('en-IN')}
                    </p>
                  )}
                </div>
              </div>
              {selected.linkedQuotation && (
                <div className="pm-meta-cell">
                  <span style={{ fontSize: 13 }}>📄</span>
                  <div style={{ minWidth: 0 }}>
                    <p className="pm-meta-lbl">Linked Quotation</p>
                    <p className="pm-meta-val" style={{ color: '#2563eb' }}>{selected.linkedQuotation.quotationNumber}</p>
                    <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>
                      ₹{Number(selected.linkedQuotation.totalAmount).toLocaleString('en-IN')} · <span style={{ textTransform: 'capitalize' }}>{selected.linkedQuotation.status}</span>
                    </p>
                  </div>
                </div>
              )}
              <div className="pm-meta-cell">
                <FiCalendar size={13} className="pm-meta-ico" />
                <div><p className="pm-meta-lbl">Start Date</p><p className="pm-meta-val">{fmt(selected.startDate)}</p></div>
              </div>
              <div className="pm-meta-cell">
                <FiCalendar size={13} className="pm-meta-ico" />
                <div><p className="pm-meta-lbl">Expected Completion</p><p className="pm-meta-val">{fmt(selected.endDate)}</p></div>
              </div>
              {selected.address && (
                <div className="pm-meta-cell">
                  <FiMapPin size={13} className="pm-meta-ico" />
                  <div><p className="pm-meta-lbl">Location</p><p className="pm-meta-val">{selected.address}</p></div>
                </div>
              )}
            </div>

            {/* Stages pipeline with quick-update */}
            <div className="pm-stages-section">
              <h3 className="pm-section-h">Project Stages <span className="pm-section-hint">— Site Visit and Closure can be updated manually; other stages auto-update</span></h3>
              <div className="pm-stages-track">
                {selStages.map((stage, i) => {
                  const done      = stage.status === 'Completed';
                  const active    = stage.status === 'In Progress';
                  const saving    = savingStage === stage.key;
                  const isManual  = MANUAL_STAGE_KEYS.includes(stage.key);
                  const stageData = selected.stages?.find(x => x.key === stage.key);
                  return (
                    <div key={stage.key} className="pm-stage">
                      {i > 0 && <div className={`pm-stage-connector ${done ? 'done' : ''}`} />}
                      <div
                        className={`pm-stage-node ${done ? 'done' : active ? 'active' : ''} ${saving ? 'saving' : ''} ${!isManual || done ? 'pm-stage-node--auto' : ''}`}
                        title={isManual ? (done ? 'Completed' : 'Click to mark Completed') : `Auto-updated by ${stageData?.completedBy || 'system'}`}
                        onClick={() => {
                          if (!isManual || done) return;
                          quickUpdateStage(stage.key, 'Completed');
                        }}
                        style={!isManual || done ? { cursor: 'default' } : {}}
                      >
                        {saving ? '⟳' : done ? <FiCheck size={14} /> : <span className="pm-stage-icon">{stage.icon}</span>}
                      </div>
                      <p className="pm-stage-lbl">{stage.label}</p>
                      {stage.date && <p className="pm-stage-date">{fmt(stage.date)}</p>}
                      {stageData?.completedBy && done && (
                        <p className="pm-stage-by" title="Completed by">{stageData.completedBy}</p>
                      )}
                      <p className={`pm-stage-stat ${done ? 'done' : active ? 'active' : 'pending'}`}>
                        {stage.status || 'Pending'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bottom 3-col grid */}
            <div className="pm-bottom">
              {/* Project Info */}
              <div className="pm-info-card">
                <div className="pm-card-header">
                  <h4>Project Information</h4>
                  <button className="pm-card-edit-btn" onClick={() => openEdit(selected)}><FiEdit2 size={11} /> Edit</button>
                </div>
                <table className="pm-info-tbl">
                  <tbody>
                    <tr><td>Project ID</td><td><strong>{projectId}</strong></td></tr>
                    <tr><td>Project Name</td><td>{selected.title}</td></tr>
                    <tr><td>Customer</td><td>{selected.client?.name || selClient?.name || '—'}</td></tr>
                    <tr><td>Created By</td><td>{selected.designer?.name || '—'}</td></tr>
                    <tr><td>Final Budget</td><td><strong>{selected.budget ? `₹ ${Number(selected.budget).toLocaleString('en-IN')}` : '—'}</strong></td></tr>
                    {selected.quotationBudget > 0 && <tr><td>Quotation Budget</td><td style={{ color: '#64748b' }}>₹ {Number(selected.quotationBudget).toLocaleString('en-IN')}</td></tr>}
                    <tr><td>Start Date</td><td>{fmt(selected.startDate)}</td></tr>
                    <tr><td>Expected End</td><td>{fmt(selected.endDate)}</td></tr>
                    <tr><td>Status</td><td>
                      <span className="pm-status-pill" style={{ background: selStatus?.bg, color: selStatus?.color, border: `1px solid ${selStatus?.border}`, fontSize: '0.72rem' }}>
                        <span className="pm-sdot" style={{ background: selStatus?.color }} />{selStatus?.label}
                      </span>
                    </td></tr>
                    {selected.address && <tr><td>Location</td><td>{selected.address}</td></tr>}
                    {selected.description && <tr><td>Notes</td><td style={{ color: '#64748b', fontSize: '0.82rem' }}>{selected.description}</td></tr>}
                    {selected.team?.length > 0 && (
                      <tr><td>Team</td><td>
                        <div className="pm-team-display">
                          {selected.team.map((t, i) => (
                            <div key={i} className="pm-team-chip">
                              <div className="pm-team-chip-avatar" style={{ background: avatarColor(t.user?.name || '') }}>
                                {(t.user?.name || '?')[0].toUpperCase()}
                              </div>
                              <div>
                                <div className="pm-team-chip-name">{t.user?.name || '—'}</div>
                                <div className="pm-team-chip-role">{t.role || '—'}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Progress ring */}
              <div className="pm-info-card pm-ring-card">
                <h4>Stage Progress Overview</h4>
                <div className="pm-ring-wrap">
                  <svg viewBox="0 0 120 120" width="140" height="140">
                    <circle cx="60" cy="60" r="48" fill="none" stroke="#f1f5f9" strokeWidth="11" />
                    <circle cx="60" cy="60" r="48" fill="none" stroke="#10b981" strokeWidth="11"
                      strokeDasharray={`${(completedCount / STAGES.length) * 301.6} 301.6`}
                      strokeLinecap="round" transform="rotate(-90 60 60)" />
                    <circle cx="60" cy="60" r="48" fill="none" stroke="#f59e0b" strokeWidth="11"
                      strokeDasharray={`${(inProgressCount / STAGES.length) * 301.6} 301.6`}
                      strokeLinecap="round"
                      transform={`rotate(${-90 + (completedCount / STAGES.length) * 360} 60 60)`} />
                  </svg>
                  <div className="pm-ring-center">
                    <span className="pm-ring-big">{completedCount} / {STAGES.length}</span>
                    <span className="pm-ring-tiny">Stages Done</span>
                  </div>
                </div>
                <div className="pm-legend">
                  <div className="pm-legend-row"><span style={{ background: '#10b981' }} /> Completed <strong>{completedCount} ({Math.round(completedCount / STAGES.length * 100)}%)</strong></div>
                  <div className="pm-legend-row"><span style={{ background: '#f59e0b' }} /> In Progress <strong>{inProgressCount} ({Math.round(inProgressCount / STAGES.length * 100)}%)</strong></div>
                  <div className="pm-legend-row"><span style={{ background: '#cbd5e1' }} /> Pending <strong>{pendingCount} ({Math.round(pendingCount / STAGES.length * 100)}%)</strong></div>
                </div>
              </div>

            </div>
          </main>
        ) : (
          <main className="pm-detail pm-no-sel">
            <div style={{ fontSize: '3rem' }}>📐</div>
            <h3>Select a Project</h3>
            <p>Choose a project from the panel to view details.</p>
            <button className="pm-new-btn" style={{ marginTop: '1rem' }} onClick={openAdd}><FiPlus size={13} /> New Project</button>
          </main>
        )}
      </div>


      {/* ── MODAL ── */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Project' : 'New Project'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX size={14} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Project Title *</label>
                  <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="e.g. 3 BHK Interior Work" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Client</label>
                  <select value={form.client} onChange={async e => {
                    const clientId = e.target.value;
                    setForm(f => ({ ...f, client: clientId, linkedQuotation: '' }));
                    setClientQuotations([]);
                    if (clientId) {
                      setLoadingQuotations(true);
                      try {
                        const r = await API.get(`/quotations?client=${clientId}`);
                        setClientQuotations(r.data || []);
                      } catch { setClientQuotations([]); }
                      finally { setLoadingQuotations(false); }
                    }
                  }}>
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Quotation Budget (₹)</label>
                  <input
                    type="number"
                    value={form.quotationBudget || 0}
                    readOnly
                    style={{ background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }}
                    placeholder="Auto-filled from quotation"
                  />
                </div>
              </div>

              {/* Quotation link — read-only if already linked, picker if not */}
              {form.client && (
                <div className="form-group pm-quo-link-group">
                  <label>Linked Quotation</label>
                  {form.linkedQuotation ? (() => {
                    // Already linked — show as read-only info card
                    const linked = editing?.linkedQuotation;
                    const quoNum = linked?.quotationNumber || '—';
                    const quoAmt = form.quotationBudget || linked?.totalAmount || 0;
                    const quoStatus = linked?.status || '';
                    const statusColor = { draft: '#64748b', sent: '#2563eb', approved: '#16a34a', rejected: '#dc2626' }[quoStatus] || '#64748b';
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '0.6rem 0.9rem' }}>
                        <span style={{ fontSize: '1rem' }}>📄</span>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.88rem' }}>{quoNum}</span>
                          {quoAmt > 0 && <span style={{ marginLeft: 8, color: '#16a34a', fontWeight: 600, fontSize: '0.83rem' }}>₹{Number(quoAmt).toLocaleString('en-IN')}</span>}
                          {quoStatus && <span style={{ marginLeft: 8, fontSize: '0.75rem', fontWeight: 600, color: statusColor, textTransform: 'capitalize' }}>● {quoStatus}</span>}
                        </div>
                        <button
                          type="button"
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, padding: '0.2rem 0.4rem' }}
                          onClick={() => setForm(f => ({ ...f, linkedQuotation: '', quotationBudget: 0 }))}
                          title="Unlink quotation"
                        >✕ Unlink</button>
                      </div>
                    );
                  })() : (
                    // Not linked — show picker
                    <>
                      {loadingQuotations && <span className="pm-quo-loading">Loading…</span>}
                      {!loadingQuotations && clientQuotations.length === 0 ? (
                        <div className="pm-quo-empty">No quotations found for this customer</div>
                      ) : (
                        <select
                          value=""
                          onChange={e => {
                            const qid = e.target.value;
                            const quo = clientQuotations.find(q => q._id === qid);
                            if (!quo) return;
                            setForm(f => ({
                              ...f,
                              linkedQuotation: qid,
                              quotationBudget: quo.totalAmount || 0,
                            }));
                          }}
                        >
                          <option value="">— Select quotation (optional) —</option>
                          {clientQuotations.map(q => (
                            <option key={q._id} value={q._id}>
                              {q.quotationNumber} · ₹{Number(q.totalAmount).toLocaleString('en-IN')} · {q.status}
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Final Budget — always editable */}
              <div className="form-group">
                <label>Final Budget (₹) <span style={{ fontWeight: 400, fontSize: '0.78rem', color: '#64748b' }}>— admin editable, used as project value</span></label>
                <input
                  type="number"
                  min="0"
                  value={form.budget}
                  onChange={e => setForm({ ...form, budget: e.target.value === '' ? '' : Math.max(0, Number(e.target.value)) })}
                  placeholder={form.quotationBudget ? `Quotation: ₹${Number(form.quotationBudget).toLocaleString('en-IN')}` : 'Enter final budget'}
                />
              </div>

              <div className="form-group">
                <label>Address / Site</label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Site address" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={e => {
                      const startDate = e.target.value;
                      const endDate = form.endDate && startDate && form.endDate < startDate ? '' : form.endDate;
                      setForm({ ...form, startDate, endDate });
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Expected Completion</label>
                  <input
                    type="date"
                    value={form.endDate}
                    min={form.startDate || undefined}
                    onChange={e => setForm({ ...form, endDate: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description / Notes</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Project notes..." />
              </div>
              {/* ── Team Assignment ── */}
              <div className="pm-team-section">
                <div className="pm-team-header">
                  <p className="pm-stages-label" style={{ margin: 0 }}>Team Assignment</p>
                  <button type="button" className="pm-team-add-btn" onClick={addTeamMember}>
                    <FiPlus size={12} /> Add Member
                  </button>
                </div>
                {form.team.length === 0 && (
                  <p className="pm-team-empty">No team members assigned. Click "Add Member" to assign roles.</p>
                )}
                {form.team.map((member, idx) => {
                  const otherSelectedUsers = form.team
                    .filter((_, i) => i !== idx)
                    .map(m => m.user)
                    .filter(Boolean);
                  const filteredUsers = member.role
                    ? users.filter(u => u.role?.name === member.role && !otherSelectedUsers.includes(u._id))
                    : [];
                  return (
                  <div key={idx} className="pm-team-row">
                    <select
                      className="pm-team-select"
                      value={member.role}
                      onChange={e => { const team = [...form.team]; team[idx] = { ...team[idx], role: e.target.value, user: '' }; setForm(f => ({ ...f, team })); }}
                    >
                      <option value="">Select Role...</option>
                      {roles.map(r => <option key={r._id} value={r.name}>{r.name}</option>)}
                    </select>
                    <select
                      className="pm-team-select"
                      value={member.user}
                      onChange={e => updateTeam(idx, 'user', e.target.value)}
                      disabled={!member.role}
                    >
                      <option value="">{member.role ? (filteredUsers.length ? 'Select User...' : 'No users for this role') : 'Select role first...'}</option>
                      {filteredUsers.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                    </select>
                    <button type="button" className="pm-team-remove-btn" onClick={() => removeTeamMember(idx)}>
                      <FiX size={13} />
                    </button>
                  </div>
                  );
                })}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">{editing ? 'Update Project' : 'Create Project'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
